import React, { useEffect } from "react";

import { CodeMirrorViewContext, useCodeMirrorViewContext } from "./codemirror-view-context.js";
import { EditingContextProvider, useEditingContext } from "./contexts/editing-context.js";
import { SelectionContextProvider, useSelectionContext } from "./contexts/selection-context.js";
import { TableProvider } from "./contexts/table-context.js";
import { useTableContext } from "./contexts/table-context.js";
import { TabularProvider, useTabularContext } from "./contexts/tabular-context.js";
import { TableGeneratorHelpModal } from "./help-modal.js";
import { Table } from "./table.js";
import { Toolbar } from "./toolbar/toolbar.js";
import { ColumnWidthModal } from "./toolbar/column-width-modal/modal.js";
import { parser } from "components/organisms/manuscript/overleaf/languages/latex/visual/latex.mjs";
import { typesetNodeIntoElement } from "../utils/typeset-content.js";

function TypesetFragment({ range, className, as: Component = "div" }) {
    const ref = React.useRef(null);
    const view = useCodeMirrorViewContext();

    useEffect(() => {
        if (!range || !ref.current) {
            return;
        }
        const text = view.state.sliceDoc(range.from, range.to);
        const tree = parser.parse(text);
        ref.current.innerHTML = "";
        typesetNodeIntoElement(tree.topNode, ref.current, text.substring.bind(text));
    }, [range, view]);

    return <Component className={className} ref={ref} />;
}

function TableScaffold() {
    const { tableEnvironment } = useTableContext();

    if (!tableEnvironment?.caption && !tableEnvironment?.footnotes?.length) {
        return <Table />;
    }

    return (
        <>
            {tableEnvironment.caption ? (
                <div className="table-generator-caption">
                    {tableEnvironment.caption.content ? (
                        <TypesetFragment
                            as="span"
                            className="table-generator-caption-text"
                            range={tableEnvironment.caption.content}
                        />
                    ) : null}
                    {tableEnvironment.label?.content ? (
                        <span className="table-generator-caption-label">
                            <span className="table-generator-caption-label-icon">🏷</span>
                            <TypesetFragment
                                as="span"
                                className="table-generator-caption-label-text"
                                range={tableEnvironment.label.content}
                            />
                        </span>
                    ) : null}
                </div>
            ) : null}
            <Table />
            {tableEnvironment.footnotes?.length ? (
                <div className="table-generator-footnotes">
                    {tableEnvironment.footnotes.map((footnote) => (
                        <div className="table-generator-footnote" key={`${footnote.from}-${footnote.to}`}>
                            {footnote.marker ? (
                                <sup className="table-generator-footnote-marker">
                                    <TypesetFragment as="span" range={footnote.marker} />
                                </sup>
                            ) : null}
                            {footnote.content ? (
                                <TypesetFragment
                                    className="table-generator-footnote-content"
                                    range={footnote.content}
                                />
                            ) : null}
                        </div>
                    ))}
                </div>
            ) : null}
        </>
    );
}

class TableGeneratorErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        console.error("table-generator render failed", error);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="table-generator table-generator-error-container">
                    <div className="notification notification-type-info" role="alert">
                        <div className="notification-icon">i</div>
                        <div className="notification-content-and-cta">
                            <div className="notification-content">
                                <p>
                                    <strong>This table can't be displayed right now.</strong>
                                </p>
                                <p>Some table elements are not supported by the visual renderer yet.</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

function TabularWrapper() {
    const { setSelection, selection } = useSelectionContext();
    const { commitCellData, cellData } = useEditingContext();
    const { ref } = useTabularContext();
    const view = useCodeMirrorViewContext();

    useEffect(() => {
        const listener = (event) => {
            if (
                !ref.current?.contains(event.target) &&
                !event.target.closest?.(".table-generator-help-modal") &&
                !event.target.closest?.(".table-generator-width-modal")
            ) {
                if (selection) {
                    setSelection(null);
                }
                if (cellData) {
                    commitCellData();
                }
            } else {
                view.dispatch({});
            }
        };
        window.addEventListener("mousedown", listener);
        return () => window.removeEventListener("mousedown", listener);
    }, [cellData, commitCellData, ref, selection, setSelection, view]);

    return (
        <div className="table-generator" ref={ref}>
            {!view.state.readOnly && <Toolbar />}
            <TableScaffold />
        </div>
    );
}

export function Tabular({ tabularNode, view, tableNode, parsedTableData, directTableChild }) {
    return (
        <TableGeneratorErrorBoundary>
            <CodeMirrorViewContext.Provider value={view}>
                <TabularProvider>
                    <TableProvider
                        tabularNode={tabularNode}
                        tableData={parsedTableData}
                        tableNode={tableNode}
                        directTableChild={directTableChild}
                        view={view}
                    >
                        <SelectionContextProvider>
                            <EditingContextProvider>
                                <TabularWrapper />
                            </EditingContextProvider>
                            <ColumnWidthModal />
                        </SelectionContextProvider>
                    </TableProvider>
                    <TableGeneratorHelpModal />
                </TabularProvider>
            </CodeMirrorViewContext.Provider>
        </TableGeneratorErrorBoundary>
    );
}
